import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { uploadMediaFile } from '@/hooks/useUpload';

// ============================================================
// Superadmin — Gestion des Formations.
// Le superadmin crée les catégories, les sous-catégories, puis les
// cours (titre, description, vidéo YouTube ou fichier vidéo, PDF
// optionnel). Chaque cours affiche ses statistiques (inscriptions).
// Les commerçants consomment ce catalogue et s'inscrivent.
// ============================================================

interface Category { id: number; titre: string; description: string | null; image: string | null; }
interface SubCategory { id: number; idformationcategorie: number; titre: string; description: string | null; }
interface Course {
  id: number; id_formateur: number | null; titre: string; description: string | null;
  image: string | null; prix: number | null; status: string | null;
  categorie: string | null; souscategorie: string | null; duree: string | null;
  video_url: string | null; pdf_url: string | null;
}

const inputClass = 'w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:border-primary-300 transition-colors';
const labelClass = 'block text-xs font-medium text-foreground-600 mb-1';

export default function SuperAdminFormationsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState<Category[]>([]);
  const [subs, setSubs] = useState<SubCategory[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [enrollments, setEnrollments] = useState<Record<number, number>>({});
  const [enrollRows, setEnrollRows] = useState<{ cours_id: number; status: string | null; granted_at: string | null }[]>([]);
  const [statsPeriod, setStatsPeriod] = useState<'7' | '30' | 'all'>('30');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [selectedCat, setSelectedCat] = useState<number | null>(null);
  const [selectedSub, setSelectedSub] = useState<number | null>(null);

  const [newCat, setNewCat] = useState({ titre: '', description: '' });
  const [newSub, setNewSub] = useState({ titre: '', description: '' });
  const [courseForm, setCourseForm] = useState({
    titre: '', description: '', prix: '0', duree: '', video_url: '', pdf_url: '', image: '',
  });
  const [showCourseForm, setShowCourseForm] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [catRes, subRes, courseRes, accessRes] = await Promise.all([
        supabase.from('formationcategorie').select('id, titre, description, image').order('titre'),
        supabase.from('formationsouscategorie').select('id, idformationcategorie, titre, description').order('titre'),
        supabase.from('formations').select('*').order('titre'),
        supabase.from('cours_access').select('cours_id, status, granted_at'),
      ]);
      setCategories((catRes.data as Category[]) || []);
      setSubs((subRes.data as SubCategory[]) || []);
      setCourses((courseRes.data as Course[]) || []);
      const rows = (accessRes.data as { cours_id: number; status: string | null; granted_at: string | null }[]) || [];
      setEnrollRows(rows);
      const counts: Record<number, number> = {};
      rows.forEach((r) => {
        counts[r.cours_id] = (counts[r.cours_id] || 0) + 1;
      });
      setEnrollments(counts);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const currentCat = useMemo(() => categories.find((c) => c.id === selectedCat) || null, [categories, selectedCat]);
  const currentSub = useMemo(() => subs.find((s) => s.id === selectedSub) || null, [subs, selectedSub]);
  const catSubs = useMemo(() => subs.filter((s) => s.idformationcategorie === selectedCat), [subs, selectedCat]);
  const subCourses = useMemo(
    () => courses.filter((c) => currentSub && c.souscategorie === currentSub.titre),
    [courses, currentSub],
  );

  // ===== Statistiques d'inscription (par cours + par période) =====
  const enrollmentStats = useMemo(() => {
    const limit = statsPeriod === 'all' ? null : new Date(Date.now() - parseInt(statsPeriod, 10) * 86400000);
    const inPeriod = enrollRows.filter((r) => {
      if (!limit) return true;
      if (!r.granted_at) return false;
      return new Date(r.granted_at) >= limit;
    });
    const total = inPeriod.length;
    const active = inPeriod.filter((r) => r.status !== 'revoked').length;

    const perCourse = new Map<number, number>();
    inPeriod.forEach((r) => perCourse.set(r.cours_id, (perCourse.get(r.cours_id) || 0) + 1));

    const ranked = Array.from(perCourse.entries())
      .map(([courseId, count]) => ({
        courseId,
        count,
        title: courses.find((c) => c.id === courseId)?.titre || `Cours #${courseId}`,
      }))
      .sort((a, b) => b.count - a.count);

    // Historique 6 mois
    const buckets: Record<string, number> = {};
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      buckets[`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`] = 0;
    }
    enrollRows.forEach((r) => {
      if (!r.granted_at) return;
      const d = new Date(r.granted_at);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (buckets[key] !== undefined) buckets[key] += 1;
    });
    const max = Math.max(1, ...Object.values(buckets));
    const history = Object.entries(buckets).map(([key, value]) => ({
      key,
      label: new Date(`${key}-01`).toLocaleDateString('fr-FR', { month: 'short' }),
      value,
      pct: Math.round((value / max) * 100),
    }));

    return { total, active, revoked: total - active, ranked: ranked.slice(0, 6), history };
  }, [enrollRows, courses, statsPeriod]);

  const addCategory = async () => {
    if (!newCat.titre.trim()) return;
    try {
      const { error: e } = await supabase.from('formationcategorie').insert({
        titre: newCat.titre.trim(), description: newCat.description.trim(), user_id: user?.id ?? 0,
      });
      if (e) throw e;
      setNewCat({ titre: '', description: '' });
      await load();
    } catch (err) { setError(err instanceof Error ? err.message : 'Erreur'); }
  };

  const deleteCategory = async (id: number) => {
    try {
      await supabase.from('formationcategorie').delete().eq('id', id);
      if (selectedCat === id) setSelectedCat(null);
      await load();
    } catch (err) { setError(err instanceof Error ? err.message : 'Erreur'); }
  };

  const addSub = async () => {
    if (!selectedCat || !newSub.titre.trim()) return;
    try {
      const { error: e } = await supabase.from('formationsouscategorie').insert({
        idformationcategorie: selectedCat, titre: newSub.titre.trim(), description: newSub.description.trim(), user_id: user?.id ?? 0,
      });
      if (e) throw e;
      setNewSub({ titre: '', description: '' });
      await load();
    } catch (err) { setError(err instanceof Error ? err.message : 'Erreur'); }
  };

  const deleteSub = async (id: number) => {
    try {
      await supabase.from('formationsouscategorie').delete().eq('id', id);
      if (selectedSub === id) setSelectedSub(null);
      await load();
    } catch (err) { setError(err instanceof Error ? err.message : 'Erreur'); }
  };

  const handlePdf = async (file: File) => {
    setUploading(true);
    try {
      const url = await uploadMediaFile(file, 'formations');
      setCourseForm((f) => ({ ...f, pdf_url: url }));
    } catch (err) { setError(err instanceof Error ? err.message : 'Erreur upload PDF'); } finally { setUploading(false); }
  };

  const handleImage = async (file: File) => {
    setUploading(true);
    try {
      const url = await uploadMediaFile(file, 'formations');
      setCourseForm((f) => ({ ...f, image: url }));
    } catch (err) { setError(err instanceof Error ? err.message : 'Erreur upload image'); } finally { setUploading(false); }
  };

  const addCourse = async () => {
    if (!currentCat || !currentSub || !courseForm.titre.trim()) return;
    try {
      const { error: e } = await supabase.from('formations').insert({
        id_formateur: user?.id ?? 0,
        titre: courseForm.titre.trim(),
        description: courseForm.description.trim(),
        image: courseForm.image,
        video_url: courseForm.video_url.trim(),
        pdf_url: courseForm.pdf_url,
        prix: parseFloat(courseForm.prix) || 0,
        duree: courseForm.duree.trim(),
        categorie: currentCat.titre,
        souscategorie: currentSub.titre,
        status: 'published',
      });
      if (e) throw e;
      setCourseForm({ titre: '', description: '', prix: '0', duree: '', video_url: '', pdf_url: '', image: '' });
      setShowCourseForm(false);
      await load();
    } catch (err) { setError(err instanceof Error ? err.message : 'Erreur'); }
  };

  const deleteCourse = async (id: number) => {
    try {
      await supabase.from('formations').delete().eq('id', id);
      await load();
    } catch (err) { setError(err instanceof Error ? err.message : 'Erreur'); }
  };

  return (
    <div className="p-4 md:p-6">
      <div className="mb-6">
        <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">
          <i className="ri-graduation-cap-line mr-2 text-primary-500"></i>Formations
        </h2>
        <p className="text-sm text-foreground-500 mt-1">
          Créez les catégories, sous-catégories et cours. Les commerçants les retrouvent dans leur espace Formation.
        </p>
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 text-red-600 text-sm flex items-center gap-2">
          <i className="ri-error-warning-line"></i>{error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
      ) : (
        <>
        {/* Tableau de bord des inscriptions */}
        <div className="mb-6 bg-background-50 border border-background-200/70 rounded-xl p-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <h3 className="text-sm font-semibold text-foreground-900 flex items-center gap-2">
              <i className="ri-bar-chart-2-line text-primary-500"></i>Statistiques des inscriptions
            </h3>
            <div className="flex items-center gap-0.5 bg-background-100 rounded-full p-0.5 self-start">
              {(['7', '30', 'all'] as const).map((p) => (
                <button key={p} onClick={() => setStatsPeriod(p)} className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${statsPeriod === p ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500'}`}>
                  {p === 'all' ? 'Tout' : `${p} jours`}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
            {[
              { label: 'Inscriptions (période)', value: enrollmentStats.total, icon: 'ri-user-add-line', color: 'bg-primary-100 text-primary-700' },
              { label: 'Actives', value: enrollmentStats.active, icon: 'ri-user-follow-line', color: 'bg-accent-100 text-accent-700' },
              { label: 'Révoquées', value: enrollmentStats.revoked, icon: 'ri-user-unfollow-line', color: 'bg-red-50 text-red-500' },
              { label: 'Cours publiés', value: courses.length, icon: 'ri-play-list-line', color: 'bg-secondary-100 text-secondary-700' },
            ].map((s) => (
              <div key={s.label} className="bg-background-100/50 rounded-lg p-3">
                <div className={`w-7 h-7 rounded-lg ${s.color} flex items-center justify-center mb-2`}><i className={`${s.icon} text-xs`}></i></div>
                <div className="text-lg font-bold font-heading text-foreground-950">{s.value}</div>
                <div className="text-[11px] text-foreground-500">{s.label}</div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Historique 6 mois */}
            <div>
              <p className="text-xs font-medium text-foreground-500 mb-2">Historique (6 mois)</p>
              <div className="h-28 flex items-end gap-2">
                {enrollmentStats.history.map((m) => (
                  <div key={m.key} className="flex-1 flex flex-col items-center gap-1">
                    <span className="text-[10px] text-foreground-400">{m.value}</span>
                    <div className="w-full bg-background-200/70 rounded-t-md" style={{ height: `${Math.max(4, m.pct)}%` }}>
                      <div className="w-full h-full bg-primary-500 rounded-t-md" />
                    </div>
                    <span className="text-[10px] text-foreground-400 capitalize">{m.label}</span>
                  </div>
                ))}
              </div>
            </div>
            {/* Top cours */}
            <div>
              <p className="text-xs font-medium text-foreground-500 mb-2">Cours les plus suivis</p>
              {enrollmentStats.ranked.length === 0 ? (
                <p className="text-xs text-foreground-400 py-6 text-center">Aucune inscription sur la période</p>
              ) : (
                <div className="space-y-2">
                  {enrollmentStats.ranked.map((r, i) => (
                    <button
                      key={r.courseId}
                      onClick={() => navigate(`/superadmin/formations/${r.courseId}`)}
                      className="w-full flex items-center gap-3 text-left hover:bg-background-100 rounded-md px-2 py-1.5 transition-colors cursor-pointer"
                    >
                      <span className="w-5 h-5 rounded-full bg-secondary-100 text-secondary-700 flex items-center justify-center text-[10px] font-bold flex-shrink-0">{i + 1}</span>
                      <span className="flex-1 min-w-0 text-xs text-foreground-700 truncate">{r.title}</span>
                      <span className="text-xs font-semibold text-foreground-900 whitespace-nowrap">{r.count}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Catégories */}
          <div className="bg-background-50 border border-background-200/70 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-foreground-900 mb-3 flex items-center gap-2"><i className="ri-folder-2-line text-primary-500"></i>Catégories</h3>
            <div className="flex gap-2 mb-3">
              <input className={inputClass} placeholder="Nouvelle catégorie" value={newCat.titre} onChange={(e) => setNewCat({ ...newCat, titre: e.target.value })} />
              <button onClick={addCategory} disabled={!newCat.titre.trim()} className="px-3 rounded-lg bg-primary-500 text-background-50 cursor-pointer disabled:opacity-50"><i className="ri-add-line"></i></button>
            </div>
            <div className="space-y-1.5 max-h-[520px] overflow-y-auto">
              {categories.length === 0 ? <p className="text-xs text-foreground-400 py-4 text-center">Aucune catégorie</p> : categories.map((c) => (
                <div key={c.id} className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${selectedCat === c.id ? 'bg-primary-50 text-primary-700' : 'hover:bg-background-100 text-foreground-700'}`}
                  onClick={() => { setSelectedCat(c.id); setSelectedSub(null); }}>
                  <span className="flex-1 text-sm truncate">{c.titre}</span>
                  <button onClick={(e) => { e.stopPropagation(); void deleteCategory(c.id); }} className="text-foreground-300 hover:text-red-500 cursor-pointer"><i className="ri-delete-bin-line text-xs"></i></button>
                </div>
              ))}
            </div>
          </div>

          {/* Sous-catégories */}
          <div className="bg-background-50 border border-background-200/70 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-foreground-900 mb-3 flex items-center gap-2"><i className="ri-folders-line text-accent-500"></i>Sous-catégories</h3>
            {!currentCat ? (
              <p className="text-xs text-foreground-400 py-8 text-center">Sélectionnez une catégorie</p>
            ) : (
              <>
                <div className="flex gap-2 mb-3">
                  <input className={inputClass} placeholder="Nouvelle sous-catégorie" value={newSub.titre} onChange={(e) => setNewSub({ ...newSub, titre: e.target.value })} />
                  <button onClick={addSub} disabled={!newSub.titre.trim()} className="px-3 rounded-lg bg-accent-500 text-white cursor-pointer disabled:opacity-50"><i className="ri-add-line"></i></button>
                </div>
                <div className="space-y-1.5 max-h-[520px] overflow-y-auto">
                  {catSubs.length === 0 ? <p className="text-xs text-foreground-400 py-4 text-center">Aucune sous-catégorie</p> : catSubs.map((s) => (
                    <div key={s.id} className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${selectedSub === s.id ? 'bg-accent-50 text-accent-700' : 'hover:bg-background-100 text-foreground-700'}`}
                      onClick={() => setSelectedSub(s.id)}>
                      <span className="flex-1 text-sm truncate">{s.titre}</span>
                      <button onClick={(e) => { e.stopPropagation(); void deleteSub(s.id); }} className="text-foreground-300 hover:text-red-500 cursor-pointer"><i className="ri-delete-bin-line text-xs"></i></button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Cours */}
          <div className="bg-background-50 border border-background-200/70 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-foreground-900 flex items-center gap-2"><i className="ri-play-circle-line text-secondary-500"></i>Cours</h3>
              {currentSub && (
                <button onClick={() => setShowCourseForm((v) => !v)} className="px-3 py-1.5 rounded-full bg-foreground-950 text-background-50 text-xs font-medium cursor-pointer whitespace-nowrap">
                  <i className="ri-add-line mr-1"></i>Nouveau cours
                </button>
              )}
            </div>

            {!currentSub ? (
              <p className="text-xs text-foreground-400 py-8 text-center">Sélectionnez une sous-catégorie</p>
            ) : (
              <>
                {showCourseForm && (
                  <div className="space-y-2 mb-4 p-3 rounded-lg bg-background-100/60">
                    <input className={inputClass} placeholder="Titre du cours" value={courseForm.titre} onChange={(e) => setCourseForm({ ...courseForm, titre: e.target.value })} />
                    <textarea className={`${inputClass} resize-none`} rows={2} placeholder="Description" value={courseForm.description} onChange={(e) => setCourseForm({ ...courseForm, description: e.target.value })} />
                    <div className="grid grid-cols-2 gap-2">
                      <input className={inputClass} placeholder="Prix (MAD)" type="number" value={courseForm.prix} onChange={(e) => setCourseForm({ ...courseForm, prix: e.target.value })} />
                      <input className={inputClass} placeholder="Durée (ex: 2 h)" value={courseForm.duree} onChange={(e) => setCourseForm({ ...courseForm, duree: e.target.value })} />
                    </div>
                    <input className={inputClass} placeholder="Lien vidéo YouTube (optionnel)" value={courseForm.video_url} onChange={(e) => setCourseForm({ ...courseForm, video_url: e.target.value })} />
                    <div className="flex items-center gap-2">
                      <label className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-dashed border-background-300/60 bg-background-50 text-xs text-foreground-600 cursor-pointer">
                        <i className="ri-file-pdf-line"></i>{courseForm.pdf_url ? 'PDF ajouté' : 'Fichier PDF (optionnel)'}
                        <input type="file" accept="application/pdf" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) void handlePdf(f); e.target.value = ''; }} />
                      </label>
                      <label className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-dashed border-background-300/60 bg-background-50 text-xs text-foreground-600 cursor-pointer">
                        <i className="ri-image-line"></i>{courseForm.image ? 'Image ajoutée' : 'Couverture'}
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleImage(f); e.target.value = ''; }} />
                      </label>
                    </div>
                    {uploading && <p className="text-[11px] text-foreground-400"><i className="ri-loader-4-line animate-spin mr-1"></i>Téléversement…</p>}
                    <button onClick={addCourse} disabled={!courseForm.titre.trim() || uploading} className="w-full py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium cursor-pointer disabled:opacity-50">
                      Enregistrer le cours
                    </button>
                  </div>
                )}

                <div className="space-y-2 max-h-[440px] overflow-y-auto">
                  {subCourses.length === 0 ? <p className="text-xs text-foreground-400 py-4 text-center">Aucun cours</p> : subCourses.map((c) => (
                    <div key={c.id} onClick={() => navigate(`/superadmin/formations/${c.id}`)} className="p-3 rounded-lg border border-background-200/70 hover:border-primary-300/60 transition-colors cursor-pointer">
                      <div className="flex items-start justify-between gap-2">
                        <h4 className="text-sm font-semibold text-foreground-900">{c.titre}</h4>
                        <button onClick={(e) => { e.stopPropagation(); void deleteCourse(c.id); }} className="text-foreground-300 hover:text-red-500 cursor-pointer"><i className="ri-delete-bin-line text-xs"></i></button>
                      </div>
                      <p className="text-xs text-foreground-500 line-clamp-2 mt-1">{c.description || 'Aucune description'}</p>
                      <div className="flex items-center flex-wrap gap-2 mt-2 text-[11px] text-foreground-400">
                        <span className="px-2 py-0.5 rounded-full bg-background-100">{c.prix && c.prix > 0 ? `${c.prix} MAD` : 'Gratuit'}</span>
                        {c.duree && <span><i className="ri-time-line mr-0.5"></i>{c.duree}</span>}
                        <span className="px-2 py-0.5 rounded-full bg-accent-100 text-accent-700">
                          <i className="ri-user-add-line mr-0.5"></i>{enrollments[c.id] || 0} inscrit(s)
                        </span>
                        {c.video_url && <i className="ri-youtube-line text-red-500 text-sm" title="Vidéo"></i>}
                        {c.pdf_url && <i className="ri-file-pdf-line text-red-500 text-sm" title="PDF"></i>}
                        <span className="ml-auto text-primary-600 font-medium">Gérer <i className="ri-arrow-right-line"></i></span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
        </>
      )}
    </div>
  );
}